import { useState } from "react";
import { useNavigate } from "react-router-dom";

/* CSS */
import styles from './TripList.module.css';

/* Hooks */
import { useIsTouch } from '../../hooks/useMediaQuery';

/* Utils */
import { formatDateRange } from "../../utils/dateUtils";
import TripContextMenu from './TripContextMenu';

/* Icons */
import default_avatar from '../../assets/icons/default_avatar.svg';
import three_dot from '../../assets/icons/three_dot.svg';

const TripList = ({ trips, onDelete, onDuplicate }) => {

    const navigate = useNavigate();
    const [contextMenu, setContextMenu] = useState(null);

    // Touch devices can't double-click reliably, so a single tap opens the trip.
    // Desktop keeps double-click (single-click there would be too eager).
    const isTouch = useIsTouch();

    if (trips.length === 0) return null;

    const handleContextMenu = (e, trip) => {
        e.preventDefault();
        setContextMenu({ trip, x: e.clientX, y: e.clientY });
    };

    // Open the context menu from the three-dot button, anchored to the button.
    // This gives touch users a reliable affordance (long-press is unreliable).
    const openMenuFromButton = (e, trip) => {
        e.stopPropagation();   // don't let the row's onClick navigate
        const rect = e.currentTarget.getBoundingClientRect();
        const menuWidth = 160;
        setContextMenu({ trip, x: rect.right - menuWidth, y: rect.bottom + 4 });
    };

    return (
        <>
            <table className={styles.tripList}>
                {/* Table header */}
                <thead>
                    <tr>
                        <th>Trip Name</th>
                        <th>Date Range</th>
                        <th>Author</th>
                    </tr>
                </thead>

                {/* Table body */}
                <tbody>
                    {trips.map((trip) => (
                        <tr
                            key={trip.id}
                            onClick={isTouch ? () => navigate(`/trip/${trip.id}`) : undefined}
                            onDoubleClick={() => navigate(`/trip/${trip.id}`)}
                            onContextMenu={(e) => handleContextMenu(e, trip)}
                            className={styles.tripListRow}
                        >
                            <td>{trip.title}</td>
                            <td>{formatDateRange(trip.fromDate, trip.toDate)}</td>
                            <td>
                                <div className={styles.lastCell}>
                                    <div className={styles.authorCell}>
                                        {(trip.members || []).map((member) => (
                                            <div
                                                key={member.user_id}
                                                className={styles.authorAvatarWrapper}
                                                data-tooltip={member.display_name}
                                            >
                                                <img
                                                    src={member.profile_picture_url || default_avatar}
                                                    alt="Author avatar"
                                                    className={styles.authorAvatar}
                                                />
                                            </div>
                                        ))}
                                    </div>

                                    <button
                                        type="button"
                                        className={styles.menuButton}
                                        onClick={(e) => openMenuFromButton(e, trip)}
                                        aria-label="Trip options"
                                    >
                                        <img src={three_dot} alt="" className={styles.menuDot} />
                                    </button>
                                </div>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>

            {contextMenu && (
                <TripContextMenu
                    trip={contextMenu.trip}
                    position={{ x: contextMenu.x, y: contextMenu.y }}
                    onClose={() => setContextMenu(null)}
                    onDuplicate={onDuplicate}
                    onDelete={onDelete}
                />
            )}
        </>
    );
}

export default TripList;
